import { reactive, computed } from 'vue'

/**
 * Singleton state for detached chat windows.
 *
 * A "detached" thread lives as a floating, draggable+resizable window
 * outside the main chat panel. Multiple can be open at once so the user
 * can watch two+ conversations side-by-side.
 *
 * State is persisted to localStorage so the user's window layout survives
 * a reload. On mobile (viewport < 768 px) detached windows are
 * automatically re-attached — the floating model doesn't fit a phone.
 */

export interface DetachedState {
    threadId: string
    x: number
    y: number
    width: number
    height: number
    z: number
}

const STORAGE_KEY = 'dz:chat:detached'
const DEFAULT_WIDTH = 360
const DEFAULT_HEIGHT = 480
const MOBILE_BREAKPOINT = 768

const detached = reactive(new Map<string, DetachedState>())
let hydrated = false

// Floating windows sit above every rail — web admin sidebar and the
// desktop chat rail are both z 100 — and below the desktop titlebar (200).
// The old counter started at 51 and only ever grew: on the desktop a freshly
// detached window landed BEHIND the chat rail it came from. restack() keeps
// the windows at Z_BASE … Z_BASE + n in their stacking order instead.
const Z_BASE = 110

/** Renumber z compactly from Z_BASE, optionally moving one window to the top. */
function restack(topId?: string) {
    const order = [...detached.values()].sort((a, b) => a.z - b.z)
    if (topId) {
        const i = order.findIndex(w => w.threadId === topId)
        if (i >= 0) order.push(order.splice(i, 1)[0])
    }
    order.forEach((w, i) => { w.z = Z_BASE + i })
}

/**
 * Seed the reactive map from localStorage on first access. Runs once
 * per tab — subsequent composable accesses return the existing map.
 */
function hydrate() {
    if (hydrated) return
    hydrated = true
    if (typeof window === 'undefined') return
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return
        for (const entry of parsed) {
            if (!entry?.threadId) continue
            detached.set(entry.threadId, {
                threadId: String(entry.threadId),
                x: Number(entry.x) || 80,
                y: Number(entry.y) || 80,
                width: Number(entry.width) || DEFAULT_WIDTH,
                height: Number(entry.height) || DEFAULT_HEIGHT,
                z: Number(entry.z) || 0
            })
        }
        restack()
    } catch {
        /* corrupted — ignore, we'll rewrite on next save */
    }

    // On mobile, auto-reattach everything (detached doesn't fit the viewport)
    if (isMobile()) detached.clear()

    // Keep a single listener that reattaches if the viewport goes mobile
    window.addEventListener('resize', onViewportResize)
}

function isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
}

function onViewportResize() {
    if (isMobile() && detached.size > 0) {
        detached.clear()
        persist()
    }
}

function persist() {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify([...detached.values()])
        )
    } catch {
        /* storage full or disabled — non-fatal */
    }
}

/** Nudge stacked windows so a newly-opened one doesn't sit exactly on top. */
function findInitialPosition(): { x: number; y: number } {
    if (typeof window === 'undefined') return { x: 80, y: 80 }
    const count = detached.size
    const offset = 24 * count
    const vw = window.innerWidth
    const vh = window.innerHeight
    // Open beside the chat panel, not on top of it (ChatPanel publishes its
    // current width as --chat-panel-width on <html>).
    const panelW = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--chat-panel-width'), 10) || 0
    return {
        // Start roughly centre-right; cascade new windows down+right
        x: Math.max(40, vw - panelW - DEFAULT_WIDTH - 24 - offset),
        y: Math.max(40, 80 + offset)
    }
    // clamp happens downstream in useDragResize
    void vh
}

/** Pop a thread out of the main panel into its own floating window. */
function detach(threadId: string) {
    if (isMobile()) return  // no-op on mobile
    if (detached.has(threadId)) {
        focus(threadId)
        return
    }
    const pos = findInitialPosition()
    detached.set(threadId, {
        threadId,
        x: pos.x,
        y: pos.y,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        z: 0
    })
    restack(threadId)
    persist()
}

/** Put a detached window back into the main panel. */
function reattach(threadId: string) {
    detached.delete(threadId)
    persist()
}

/** Bring a window to the front (bump its z-index above the rest). */
function focus(threadId: string) {
    if (!detached.has(threadId)) return
    restack(threadId)
    persist()
}

/** Commit a new rect (size / position) from useDragResize's onCommit. */
function updateRect(threadId: string, rect: { x: number; y: number; width: number; height: number }) {
    const s = detached.get(threadId)
    if (!s) return
    s.x = rect.x
    s.y = rect.y
    s.width = rect.width
    s.height = rect.height
    persist()
}

export function useDetachedWindows() {
    hydrate()
    return {
        detached: computed(() => [...detached.values()].sort((a, b) => a.z - b.z)),
        isDetached: (threadId: string) => detached.has(threadId),
        get: (threadId: string) => detached.get(threadId),
        detach,
        reattach,
        focus,
        updateRect
    }
}
